import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

// ----------------------------------------------------------------------

export default function RHFAutocomplete({ name, label, placeholder, helperText, showSelectAll, ...other }) {
  const { control, setValue } = useFormContext();

  const options = Array.isArray(other.options) ? other.options : [];
  const displayOptions = showSelectAll && other.multiple
    ? [{ label: 'Select All', value: 'all' }, ...options]
    : options;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          value={
            (() => {
              const v = field.value;
              if (other.multiple) {
                if (!Array.isArray(v)) return [];
                // map primitives in array to objects
                return v.map(val => {
                  if (typeof val === 'object' && val !== null) return val;
                  return displayOptions.find(opt => (opt.value !== undefined ? opt.value : opt._id || opt.id || opt) === val) || val;
                });
              }
              // single value
              if (v === '' || v === undefined || v === null) return null;
              if (typeof v === 'object' && v !== null) return v;
              return displayOptions.find(opt => (opt.value !== undefined ? opt.value : opt._id || opt.id || opt) === v) || null;
            })()
          }
          disableCloseOnSelect={other.multiple}
          onChange={(event, newValue) => {
            if (showSelectAll && other.multiple) {
              const selectedAll = newValue.find(item => item.value === 'all');

              if (selectedAll) {
                const currentRealValues = (field.value || []).filter(v => typeof v === 'object' ? v.value !== 'all' : v !== 'all');
                const isAllSelected = currentRealValues.length === options.length;

                if (!isAllSelected) {
                  // Select All: extract primitive values
                  const primitives = options.map(opt => opt.value !== undefined ? opt.value : opt._id || opt.id || opt);
                  setValue(name, primitives, { shouldValidate: true });
                } else {
                  // Deselect All
                  setValue(name, [], { shouldValidate: true });
                }
                return;
              }
            }

            // Helper to extract primitive value safely
            const extractPrimitive = (val) => {
              if (typeof val !== 'object' || val === null) {
                return val;
              }
              if (val.value !== undefined) {
                return val.value;
              }
              return val._id || val.id || val;
            };

            // Extract primitive values
            if (!newValue) {
              setValue(name, null, { shouldValidate: true });
            } else if (Array.isArray(newValue)) {
              const primitives = newValue.map(extractPrimitive);
              setValue(name, primitives, { shouldValidate: true });
            } else {
              const primitive = extractPrimitive(newValue);
              setValue(name, primitive, { shouldValidate: true });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
              required={false}
            />
          )}
          {...other}
          multiple={other.multiple}
          options={displayOptions}
          isOptionEqualToValue={(option, value) => {
            const optVal = option.value || option._id || option.id || option;
            const fieldVal = value?.value || value?._id || value?.id || value;
            return optVal === fieldVal;
          }}
          filterOptions={(x, state) => {
            if (!Array.isArray(x)) return [];
            const filter = createFilterOptions();
            return filter(x, state);
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (!option) return '';
            if (typeof option.label === 'string') return option.label;
            
            // Extract common names if it's an object
            return String(option.name || option.title || option.block_name || option.city_name || option.state_name || option.label || '');
          }}
        />
      )}
    />
  );
}

RHFAutocomplete.propTypes = {
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  showSelectAll: PropTypes.bool,
};
